import { JSONValue } from '@easeltv/client-sdk';
import { obj, range } from '@easeltv/utilities';

import { Field } from '../../../providers/RegistrationProvider/RegistrationProvider.helpers';
import CheckboxAtom from '../../Atoms/CheckboxAtom';
import DropdownAtom from '../../Atoms/DropdownAtom';
import TextFieldAtom from '../../Atoms/TextFieldAtom';
import { FieldType } from '../../Atoms/TextFieldAtom/TextFieldAtom.helpers';

/**
 * FormStateI
 *
 * The state of the FormBlock useReducer
 */
export type FormStateI = {
    loading: boolean;
    fields: Record<string, unknown>;
    errors: Record<string, string | undefined>;
};

/**
 * mapInputTypeToDefault
 *
 * Figures out what default value to give each field when initializing the reducer state
 *
 * @param type The type of the field
 * @returns Input's initial value
 */
export const mapInputTypeToDefault = (type: FieldType) => {
    switch (type) {
        case 'year':
            return '';
        case 'number':
            return 0;
        case 'checkbox':
            return false;
        case 'dropdown':
        case 'text':
        case 'email':
        case 'password':
        default:
            return '';
    }
};

/**
 * FormActionTypes
 *
 * All the actions that can be dispatched to the reducer
 */
export enum FormActionTypes {
    UpdateField,
    SetError,
}

/**
 * FormActions
 *
 * A map of all the actions and their payloads
 */
export type FormActions = {
    [FormActionTypes.UpdateField]: {
        id: string;
        value: unknown;
    };
    [FormActionTypes.SetError]: {
        id: string;
        value?: string;
    };
};

/**
 * initializeFormState
 *
 * Takes in seed data to return the initial state of the reducer
 *
 * @param seedData
 * @returns initial state of the reducer
 */
export const initializeFormState = (fieldsDefs: Omit<Field, 'prompt'>[]) => {
    const { fields, errors } = fieldsDefs.reduce<{
        fields: FormStateI['fields'];
        errors: FormStateI['errors'];
    }>(
        (state, field) => {
            const f = {
                ...state.fields,
                [field.id]: mapInputTypeToDefault(field.type),
            };

            const e = {
                ...state.errors,
                [field.id]: undefined,
            };

            return { fields: f, errors: e };
        },
        {
            fields: {},
            errors: {},
        },
    );
    return {
        fields,
        errors,
        loading: false,
    };
};

/**
 * FormActionI
 *
 * Contains all the Action types mapped to their payloads
 */
export type FormActionI = ActionMap<FormActions>[keyof ActionMap<FormActions>];

/**
 * formReducer
 *
 * The reducer function to resolve action into updated state
 *
 * @param state The existing state
 * @param action The action that was dispatched
 * @returns The new state
 */
export const formReducer = (
    state: FormStateI,
    action: FormActionI,
): FormStateI => {
    switch (action.type) {
        case FormActionTypes.UpdateField: {
            const { id, value } = action.payload;

            return {
                ...state,
                fields: {
                    ...state.fields,
                    [id]: value,
                },
            };
        }
        case FormActionTypes.SetError: {
            const { id, value } = action.payload;

            return {
                ...state,
                errors: {
                    ...state.errors,
                    [id]: value,
                },
            };
        }

        default:
            return state;
    }
};

/**
 * getCustomPlaceholder
 *
 * Returns a string used for placeholder text
 *
 * @param fieldType FieldType
 * @param label The default value used if no custom placeholder exists
 * @returns
 */
export const getCustomPlaceholder = (
    type: FieldType,
    label: string,
): string => {
    switch (type) {
        case 'newPassword':
            return 'Minimum 12 characters';
        default:
            return label;
    }
};

/**
 * renderFormInput
 *
 * Renders input fields based on the type in the array
 *
 * @param field field object
 * @param value current value in the state object
 * @param updateField callback function for onChange
 * @returns Input component
 */
export const renderFormInput = (
    field: Field & { label: string },
    state: FormStateI,
    updateField: (key: string) => (value: JSONValue) => void,
) => {
    const { fields, errors } = state;

    // @TODO: Figure out proper typing here...
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const value = fields[field.id] as any;

    // Field has error if a id exists in the error object
    const hasError = Boolean(errors[field.id]);

    switch (field.type) {
        case 'checkbox':
            return (
                <CheckboxAtom
                    hasError={hasError}
                    key={field.id}
                    id={field.id}
                    label={field.label}
                    required={field.required}
                    updateField={updateField(field.id)}
                    value={value}
                    errorText={errors[field.id]}
                />
            );
        case 'dropdown':
            return (
                <DropdownAtom
                    hasError={hasError}
                    key={field.id}
                    label={field.label}
                    options={field.options}
                    placeholder={field.placeholder ?? field.label}
                    required={field.required}
                    updateField={(newValue) =>
                        updateField(field.id)(newValue.toLowerCase())
                    }
                    value={value}
                    errorText={errors[field.id]}
                />
            );

        case 'year': {
            const options = range(
                new Date().getFullYear() - 120,
                new Date().getFullYear(),
            )
                .map((option) => ({
                    label: option.toString(),
                    value: option.toString(),
                }))
                .reverse();
            return (
                <DropdownAtom
                    canFilter
                    hasError={hasError}
                    key={field.id}
                    label={field.label}
                    options={options}
                    placeholder={field.placeholder ?? field.label}
                    required={field.required}
                    updateField={(newValue) =>
                        updateField(field.id)(parseInt(newValue, 10))
                    }
                    value={value}
                    errorText={errors[field.id]}
                />
            );
        }
        case 'number':
        case 'text':
        case 'email':
        case 'password':
        case 'confirmPassword':
        case 'newPassword':
        default:
            return (
                <TextFieldAtom
                    hasError={hasError}
                    key={field.id}
                    id={field.id}
                    label={field.label}
                    placeholder={getCustomPlaceholder(
                        field.type,
                        field.placeholder ?? field.label,
                    )}
                    required={field.required}
                    type={field.type}
                    updateField={updateField(field.id)}
                    value={value}
                    errorText={errors[field.id]}
                    canToggleTypeAttribute={
                        field.type === 'password' ||
                        field.type === 'newPassword' ||
                        field.type === 'confirmPassword'
                    }
                />
            );
    }
};

export function removeEmptyStringFromObject<T extends object>(x: T) {
    return (
        obj
            .entries(x)
            // Filter out entries with empty string values
            .filter(([, value]) => value !== '')
            // Reduce the resultant array of [key, value] pairs back into an object
            .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})
    );
}
